import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  InputAdornment
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Verified as VerifiedIcon,
  Star as PremiumIcon,
  ContactPhone as ContactIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { tgAccountsAPI } from '../../Api';
import PlumSvg from '../component/PlumSvg';

const TGAccountSendMessage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState('');
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState(null);

  const showAlert = (type, message) => {
    setAlert(<Alert severity={type}>{message}</Alert>);
    setTimeout(() => setAlert(null), 5000);
  };

  const loadAccount = async () => {
    try {
      const res = await tgAccountsAPI.getAccountById(id);
      setAccount(res.data?.data);
    } catch (e) {
      showAlert('error', 'Ошибка загрузки аккаунта');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      showAlert('warning', 'Введите username или номер телефона для поиска');
      return;
    }

    setSearching(true);
    try {
      const res = await tgAccountsAPI.searchUsers(id, searchQuery.trim(), 20);
      if (res.data?.success) {
        setSearchResults(res.data.data.users || []);
        if (res.data.data.users.length === 0) {
          showAlert('info', 'Пользователи не найдены');
        }
      } else {
        showAlert('error', 'Ошибка поиска пользователей');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка поиска пользователей');
    } finally {
      setSearching(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleSendMessage = async () => {
    if (!selectedUser) {
      showAlert('warning', 'Выберите получателя сообщения');
      return;
    }

    if (!message.trim()) {
      showAlert('warning', 'Введите текст сообщения');
      return;
    }

    setSending(true);
    try {
      const res = await tgAccountsAPI.sendMessage(id, selectedUser.chat_id, message.trim());
      if (res.data?.success) {
        showAlert('success', res.data.data.message || 'Сообщение успешно отправлено!');
        setMessage('');
        setSelectedUser(null);
      } else {
        showAlert('error', 'Ошибка отправки сообщения');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка отправки сообщения');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (selectedUser && message.trim()) {
        handleSendMessage();
      } else if (searchQuery.trim()) {
        handleSearch();
      }
    }
  };

  useEffect(() => {
    loadAccount();
  }, [id]);

  const getUserDisplayName = (user) => {
    if (user.username) return `@${user.username}`;
    if (user.full_name) return user.full_name;
    if (user.phone_number) return user.phone_number;
    return `User ${user.user_id}`;
  };

  const getUserSecondaryInfo = (user) => {
    const info = [];
    if (user.username && user.full_name) info.push(user.full_name);
    if (user.phone_number) info.push(user.phone_number);
    return info.join(' • ');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, p: 4, boxShadow: 3, borderRadius: 2, bgcolor: '#fefcfb' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/tg-accounts/list')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlumSvg /> Отправка сообщения
            </Typography>
            {account && (
              <Typography variant="subtitle2" color="text.secondary">
                От: {account.phone} • {account.type}
              </Typography>
            )}
          </Box>
        </Box>

        {alert}

        {/* User Search */}
        {!selectedUser && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              🔍 Поиск получателя
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Username или номер телефона"
                placeholder="@username или +7999..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button 
                variant="contained" 
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                sx={{ backgroundColor: '#683d62', minWidth: 120 }}
              >
                {searching ? <CircularProgress size={20} /> : 'Найти'}
              </Button>
            </Box>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Найденные пользователи:
                </Typography>
                <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                  {searchResults.map((user, index) => (
                    <React.Fragment key={user.user_id}>
                      <ListItem 
                        button 
                        onClick={() => handleUserSelect(user)}
                        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">
                                {getUserDisplayName(user)}
                              </Typography>
                              {user.is_verified && (
                                <Tooltip title="Верифицированный">
                                  <VerifiedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                </Tooltip>
                              )}
                              {user.is_premium && (
                                <Tooltip title="Premium">
                                  <PremiumIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                </Tooltip>
                              )}
                              {user.is_contact && (
                                <Tooltip title="В контактах">
                                  <ContactIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                </Tooltip>
                              )}
                            </Box>
                          }
                          secondary={getUserSecondaryInfo(user)}
                        />
                        <Chip label="Выбрать" size="small" />
                      </ListItem>
                      {index < searchResults.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </Paper>
        )}

        {/* Selected User & Message */}
        {selectedUser && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                📤 Отправка сообщения
              </Typography>
              <Button 
                size="small" 
                onClick={() => setSelectedUser(null)}
                color="secondary"
              >
                Изменить получателя
              </Button>
            </Box>

            {/* Selected User Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getUserDisplayName(selectedUser)}
                  {selectedUser.is_verified && (
                    <VerifiedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  )}
                  {selectedUser.is_premium && (
                    <PremiumIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  )}
                </Typography>
                {getUserSecondaryInfo(selectedUser) && (
                  <Typography variant="body2" color="text.secondary">
                    {getUserSecondaryInfo(selectedUser)}
                  </Typography>
                )}
              </Box>
              <Chip 
                label={`ID: ${selectedUser.chat_id}`} 
                size="small" 
                variant="outlined" 
              />
            </Box>

            {/* Message Input */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Текст сообщения"
              placeholder="Введите ваше сообщение..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{ mb: 2 }}
            />

            {/* Send Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/tg-accounts/list')}
              >
                Отмена
              </Button>
              <Button 
                variant="contained" 
                startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSendMessage}
                disabled={sending || !message.trim()}
                sx={{ backgroundColor: '#683d62' }}
              >
                {sending ? 'Отправка...' : 'Отправить'}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Instructions */}
        <Paper elevation={1} sx={{ p: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            💡 Инструкция
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>Введите username (например: @durov) или номер телефона (+7999...)</li>
            <li>Нажмите "Найти" для поиска пользователей</li>
            <li>Выберите получателя из списка результатов</li>
            <li>Введите текст сообщения и нажмите "Отправить"</li>
            <li>Можно использовать Enter для отправки (Shift+Enter для новой строки)</li>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default TGAccountSendMessage;
